import { useFlag } from '@calendly/feature-check-react';
import { I18n } from '@calendly/i18n';
import { InlineBanner } from '@calendly/ui/components/inline-banner';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetUserQuery } from 'common';
import { useFetchContactByEmailGQLQuery } from 'features/contacts';
import { CRM_UNIFIED_MEETING_BRIEFS_FLAG_KEY } from 'features/crm';
import { TRACKING_EVENT_NAMES, useTrack } from 'hooks/analytics_events';
import { useOpenUnifiedMeetingBriefsFromHost } from 'hooks/oap_web/use_open_unified_meeting_briefs_from_host';
import { useMeetingsPageTourContext } from './contexts/MeetingsPageTourContext';
const bannerWrapperCss = "b5a6aab";
const isOneOnOneMeeting = meeting => meeting?.attendees?.length === 2;
const getContactEmail = (meeting, currentUser) => {
  if (!currentUser?.email) return undefined;
  return currentUser.email === meeting.host?.email ? meeting.invitee?.email : meeting.host?.email;
};
const getCounterpartyName = (meeting, currentUser) => {
  const counterparty = currentUser?.email === meeting.host?.email ? meeting.invitee : meeting.host;
  return counterparty?.name;
};
const getPrimaryContactEmail = contact => {
  const firstEmail = contact?.emails?.[0];
  if (firstEmail == null) return contact?.email ?? '';
  return typeof firstEmail === 'string' ? firstEmail : firstEmail?.email ?? '';
};
const MeetingBriefContact = ({
  meeting,
  currentUser,
  onResolved
}) => {
  const contactEmail = getContactEmail(meeting, currentUser);
  const {
    data: contact,
    isLoading
  } = useFetchContactByEmailGQLQuery(contactEmail, {
    skip: !contactEmail || meeting.cancelled
  });
  useEffect(() => {
    onResolved(meeting.uuid, contact && !isLoading && !meeting.cancelled ? {
      contact,
      meeting
    } : null);
  }, [contact, isLoading, meeting, onResolved]);
  return null;
};
const getMeetingBriefTitle = (meetings, currentUser) => {
  const names = [...new Set(meetings.map(meeting => getCounterpartyName(meeting, currentUser)).filter(Boolean))];
  if (names.length === 0) {
    return I18n.t('publisher.meetings.unified_briefs.title', {
      count: meetings.length
    });
  }
  if (names.length === 1) {
    return I18n.t('publisher.meetings.unified_briefs.title_with_name', {
      count: meetings.length,
      name: names[0]
    });
  }
  return I18n.t('publisher.meetings.unified_briefs.title_with_others', {
    name: names[0],
    count: names.length - 1
  });
};
export const MeetingBriefsBanner = ({
  meetings = []
}) => {
  const isUnifiedBriefsEnabled = useFlag(CRM_UNIFIED_MEETING_BRIEFS_FLAG_KEY, false);
  const {
    data: currentUser
  } = useGetUserQuery();
  const [isDismissed, setIsDismissed] = useState(false);
  const [resolvedBriefs, setResolvedBriefs] = useState({});
  const openUnifiedMeetingBriefs = useOpenUnifiedMeetingBriefsFromHost();
  const {
    setMeetingBriefModalVisible
  } = useMeetingsPageTourContext() ?? {};
  const oneOnOneMeetings = meetings.filter(isOneOnOneMeeting);
  const onBriefResolved = useCallback((meetingUuid, brief) => {
    setResolvedBriefs(current => {
      if (current[meetingUuid] === brief) return current;
      return {
        ...current,
        [meetingUuid]: brief
      };
    });
  }, []);
  const track = useTrack();
  const handleDismiss = useCallback(() => {
    track({
      name: TRACKING_EVENT_NAMES.CONTACT_INTERACTION,
      properties: {
        location: 'meetings_page',
        action: 'dismiss_unified_brief'
      }
    });
    setIsDismissed(true);
  }, [track]);
  const availableBriefs = useMemo(() => oneOnOneMeetings.map(meeting => resolvedBriefs[meeting.uuid]).filter(Boolean), [oneOnOneMeetings, resolvedBriefs]);
  if (!isUnifiedBriefsEnabled || isDismissed) {
    return null;
  }
  const contactLookups = oneOnOneMeetings.map(meeting => <MeetingBriefContact key={meeting.uuid} meeting={meeting} currentUser={currentUser} onResolved={onBriefResolved} />);
  if (availableBriefs.length === 0) {
    return contactLookups;
  }
  const handleOpenBriefs = async () => {
    const briefsWithPrimaryContactEmail = availableBriefs.map(brief => ({
      ...brief,
      contact: {
        ...brief.contact,
        email: getPrimaryContactEmail(brief.contact)
      }
    }));
    const didOpen = await openUnifiedMeetingBriefs(briefsWithPrimaryContactEmail);
    if (didOpen) {
      setMeetingBriefModalVisible?.(true);
    }
  };
  return <>
      {contactLookups}
      <div className={bannerWrapperCss}>
        <InlineBanner data-testid="meeting-briefs-banner" variant="ai" title={getMeetingBriefTitle(availableBriefs.map(({
        meeting
      }) => meeting), currentUser)} message={I18n.t('publisher.meetings.unified_briefs.message', {
        count: availableBriefs.length
      })} hasIcon isDismissible onDismiss={handleDismiss} primaryButton={{
        'data-analytics-id': 'contacts.meetings_page.generate_unified_brief',
        decoration: 'secondary-outline',
        label: I18n.t('publisher.meetings.unified_briefs.view_briefs', {
          count: availableBriefs.length
        }),
        onClick: handleOpenBriefs,
        responsive: false
      }} />
      </div>
    </>;
};

require("./meeting_briefs_banner.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./meeting_briefs_banner.js");